import { Button as ButtonPrimitive } from "@base-ui/react/button";
import {
  type ToastObject,
  Toast as ToastPrimitive,
} from "@base-ui/react/toast";
import { cva, type VariantProps } from "class-variance-authority";
import { useId } from "react";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { Icon } from "../icon";

type ToastVariant = "default" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastData {
  action?: ToastAction;
  icon?: boolean;
  onDismiss?: () => void;
}

export interface ToastAddOptions {
  variant?: ToastVariant;
  action?: ToastAction;
  /** Show the leading icon. */
  icon?: boolean;
  onDismiss?: () => void;
  timeout?: number;
}

export interface ToastProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  variant?: ToastVariant;
  description: string;
  action?: ToastAction;
  /** Show the leading icon. */
  icon?: boolean;
  onDismiss?: () => void;
}

const toastRootStyles = cva(
  [
    "rounded-(--radius-sm)",
    "outline-none",
    "transition-[opacity,transform,height]",
    "duration-200",
    "ease-in-out",
    "motion-reduce:transition-none",
    "shadow-elevation-3",
    "[&[data-starting-style]]:translate-y-2",
    "[&[data-starting-style]]:opacity-0",
    "[&[data-ending-style]]:translate-y-2",
    "[&[data-ending-style]]:opacity-0",
    "[&[data-limited]]:pointer-events-none",
    "[&[data-limited]]:opacity-0",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-surface-inverse-surface",
          "text-surface-inverse-on-surface",
        ],
        error: ["bg-error", "text-error-on-error"],
      },
      placement: {
        static: ["relative", "w-full", "max-w-[344px]"],
        viewport: ["w-full"],
      },
    },
    defaultVariants: {
      variant: "default",
      placement: "static",
    },
  },
);

const toastContentStyles = cva(
  ["flex", "min-h-[48px]", "w-full", "items-center", "pl-(--spacing-10)"],
  {
    variants: {
      hasClose: {
        true: "pr-(--spacing-4)",
        false: "pr-(--spacing-8)",
      },
      layout: {
        inline: "gap-(--spacing-10)",
        "inline-action": "gap-(--spacing-4)",
        stacked: "items-start gap-(--spacing-10)",
      },
    },
    defaultVariants: {
      hasClose: false,
      layout: "inline",
    },
  },
);

const toastDescriptionStyles = cva([
  "m-0",
  "min-w-0",
  "font-(family-name:--font-family-body)",
  "text-(length:--font-size-body-medium)",
  "font-(number:--font-weight-regular)",
  "leading-(--font-line-height-20)",
  "tracking-(--font-letter-spacing-default)",
  "text-current",
  "overflow-hidden",
  "[display:-webkit-box]",
  "[-webkit-box-orient:vertical]",
  "[-webkit-line-clamp:2]",
]);

const toastTextFrameStyles = cva("min-w-0 flex-1 py-(--spacing-10)", {
  variants: {
    layout: {
      inline: "block",
      "inline-action": "block",
      stacked: "flex flex-col items-stretch gap-(--spacing-4)",
    },
  },
  defaultVariants: {
    layout: "inline",
  },
});

const toastLeadingIconStyles = cva("shrink-0 py-(--spacing-10)", {
  variants: {
    variant: {
      default: "text-primary-inverse-primary",
      error: "text-error-on-error",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const toastButtonStyles = cva(
  [
    "inline-flex",
    "h-(--spacing-40)",
    "shrink-0",
    "items-center",
    "justify-center",
    "gap-(--spacing-8)",
    "whitespace-nowrap",
    "rounded-(--radius-sm)",
    "border-0",
    "bg-transparent",
    "px-(--spacing-12)",
    "py-0",
    "font-(family-name:--font-family-label)",
    "text-(length:--font-size-label-large)",
    "font-(number:--font-weight-medium)",
    "leading-(--font-line-height-20)",
    "tracking-(--font-letter-spacing-default)",
    "cursor-pointer",
    "select-none",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-offset-1",
    "focus-visible:ring-button-focus-ring",
    "disabled:pointer-events-none",
    "disabled:opacity-40",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        default: [
          "text-primary-inverse-primary",
          "hover:bg-surface-inverse-surface-hover",
          "active:bg-surface-inverse-surface-pressed",
          "focus-visible:ring-offset-surface-inverse-surface",
        ],
        error: [
          "text-error-on-error",
          "hover:bg-toast-error-button-hover-background",
          "active:bg-toast-error-button-pressed-background",
          "focus-visible:ring-offset-error",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const toastCloseStyles = cva(
  [
    "inline-flex",
    "size-[48px]",
    "shrink-0",
    "items-center",
    "justify-center",
    "rounded-(--radius-sm)",
    "border-0",
    "bg-transparent",
    "p-0",
    "cursor-pointer",
    "select-none",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-offset-1",
    "focus-visible:ring-button-focus-ring",
    "disabled:pointer-events-none",
    "disabled:opacity-40",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        default: [
          "text-surface-inverse-on-surface",
          "hover:bg-surface-inverse-surface-hover",
          "active:bg-surface-inverse-surface-pressed",
          "focus-visible:ring-offset-surface-inverse-surface",
        ],
        error: [
          "text-error-on-error",
          "hover:bg-toast-error-button-hover-background",
          "active:bg-toast-error-button-pressed-background",
          "focus-visible:ring-offset-error",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const viewportStyles = cn(
  "fixed",
  "[left:0]",
  "[right:0]",
  "mx-auto",
  "bottom-(--spacing-24)",
  "z-[var(--z-layer-notification)]",
  "h-(--toast-frontmost-height)",
  "w-[min(344px,calc(100vw_-_var(--spacing-32)))]",
);

const stackedActionLabelMinLength = "Long Label".length;

type ToastButtonProps = Omit<ButtonPrimitive.Props, "className"> &
  VariantProps<typeof toastButtonStyles>;

function _ToastButton({
  children,
  variant,
  disabled = false,
  ...props
}: ToastButtonProps) {
  return (
    <ButtonPrimitive
      className={cn(toastButtonStyles({ variant }))}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      {...props}
    >
      {children}
    </ButtonPrimitive>
  );
}

type ToastCloseProps = Omit<ButtonPrimitive.Props, "className" | "children"> &
  VariantProps<typeof toastCloseStyles>;

function _ToastClose({
  variant,
  disabled = false,
  "aria-label": ariaLabel = "Dismiss notification",
  ...props
}: ToastCloseProps) {
  return (
    <ButtonPrimitive
      className={cn(toastCloseStyles({ variant }))}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : undefined}
      {...props}
    >
      <Icon name="x-close" size="sm" tone="inherit" />
    </ButtonPrimitive>
  );
}

const toastManager = ToastPrimitive.createToastManager<ToastData>();

function getToastVariant(type: string | undefined): ToastVariant {
  return type === "error" ? "error" : "default";
}

const toast = {
  add(description: string, options: ToastAddOptions = {}) {
    const {
      variant = "default",
      action,
      icon = false,
      onDismiss,
      timeout,
    } = options;
    return toastManager.add({
      description,
      type: variant,
      priority: variant === "error" ? "high" : "low",
      ...(timeout !== undefined && { timeout }),
      data: { action, icon, onDismiss },
    });
  },
  error(description: string, options: Omit<ToastAddOptions, "variant"> = {}) {
    return toast.add(description, { ...options, variant: "error" });
  },
};

interface ToastRendererProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  toast: ToastObject<ToastData>;
  onActionComplete?: () => void;
  placement?: "static" | "viewport";
}

function ToastRenderer({
  toast: toastObject,
  onActionComplete,
  placement = "static",
  ...props
}: ToastRendererProps) {
  const variant = getToastVariant(toastObject.type);
  const action = toastObject.data?.action;
  const showLeadingIcon = toastObject.data?.icon ?? false;
  const showClose = Boolean(toastObject.data?.onDismiss);
  const layout =
    action && action.label.length >= stackedActionLabelMinLength
      ? "stacked"
      : action
        ? "inline-action"
        : "inline";
  const showStackedClose = layout === "stacked" && showClose;
  const showTrailingClose = showClose && !showStackedClose;

  function handleActionClick() {
    action?.onClick();
    onActionComplete?.();
  }

  function handleDismissClick() {
    toastObject.data?.onDismiss?.();
  }

  return (
    <ToastPrimitive.Root
      toast={toastObject}
      swipeDirection={["down", "right"]}
      role={variant === "error" ? "alert" : "status"}
      aria-live={variant === "error" ? "assertive" : "polite"}
      className={cn(toastRootStyles({ variant, placement }))}
      {...props}
    >
      <ToastPrimitive.Content
        className={cn(toastContentStyles({ hasClose: showClose, layout }))}
      >
        {showLeadingIcon && (
          <span
            data-slot="toast-leading-icon"
            className={cn(toastLeadingIconStyles({ variant }))}
          >
            <Icon
              name={
                variant === "error"
                  ? "circle-exclamation"
                  : "circle-check-regular"
              }
              size="md"
              tone="inherit"
            />
          </span>
        )}
        <div className={cn(toastTextFrameStyles({ layout }))}>
          <ToastPrimitive.Description
            className={cn(toastDescriptionStyles())}
          />
          {action && layout === "stacked" && (
            <div className="flex w-full items-center justify-end">
              <ToastPrimitive.Action
                render={
                  <_ToastButton variant={variant} onClick={handleActionClick} />
                }
              >
                {action.label}
              </ToastPrimitive.Action>
              {showStackedClose && (
                <ToastPrimitive.Close
                  render={
                    <_ToastClose
                      variant={variant}
                      aria-hidden={undefined}
                      onClick={handleDismissClick}
                    />
                  }
                />
              )}
            </div>
          )}
        </div>
        {action && layout === "inline-action" && (
          <ToastPrimitive.Action
            render={
              <_ToastButton variant={variant} onClick={handleActionClick} />
            }
          >
            {action.label}
          </ToastPrimitive.Action>
        )}
        {showTrailingClose && (
          <ToastPrimitive.Close
            render={
              <_ToastClose
                variant={variant}
                aria-hidden={undefined}
                onClick={handleDismissClick}
              />
            }
          />
        )}
      </ToastPrimitive.Content>
    </ToastPrimitive.Root>
  );
}

/**
 * Toast notification built on Base UI Toast for transient feedback.
 *
 * Viewport-rendered toasts are positioned bottom-center. Supports default and
 * error variants, optional action, leading icon, and dismiss button. Render
 * directly for static previews or wrap the app in `AssetWatchUiProvider` and
 * use `toast.add` / `toast.error` for app notifications.
 *
 * @example
 * <Toast description="Single-line toast" />
 * <Toast variant="error" description="Single-line toast" icon />
 * <Toast
 *   description="Two-line toast with longer action and dismiss button"
 *   action={{ label: "Long Label", onClick: handleAction }}
 *   onDismiss={handleDismiss}
 * />
 * <Toast variant="error" description="Single-line toast" onDismiss={handleDismiss} />
 */
function Toast({
  variant,
  description,
  action,
  icon,
  onDismiss,
  ...props
}: ToastProps) {
  const id = useId();
  return (
    <ToastPrimitive.Provider timeout={0} limit={1}>
      <ToastRenderer
        toast={{
          id,
          description,
          type: variant ?? "default",
          priority: variant === "error" ? "high" : "low",
          data: { action, icon, onDismiss },
        }}
        {...props}
      />
    </ToastPrimitive.Provider>
  );
}

function ToastViewport() {
  const manager = ToastPrimitive.useToastManager<ToastData>();
  const portalHost = usePortalHost();

  return (
    <ToastPrimitive.Portal container={portalHost}>
      <ToastPrimitive.Viewport className={viewportStyles}>
        {manager.toasts.map((toastObject) => (
          <ToastRenderer
            key={toastObject.id}
            onActionComplete={() => manager.close(toastObject.id)}
            toast={toastObject}
            placement="viewport"
          />
        ))}
      </ToastPrimitive.Viewport>
    </ToastPrimitive.Portal>
  );
}

export { Toast, ToastViewport, toast, toastManager };
export type { ToastVariant };
